import { createSlice, PayloadAction, createEntityAdapter, EntityState } from "@reduxjs/toolkit";
import { RootState } from '../../../app/store';
import {CaseOverviewRecord } from "../../../lib/interfaces/types";
import {fetchCaseOverview as fetchCaseOverviewThunk } from "../../../thunks/AnalysisStep";
import { CaseOverviewStepResponse } from "../../../api/AnalysisStep"


export interface CaseOverviewDataState extends EntityState<CaseOverviewRecord, string> {
    loadingStatus: 'idle' | 'pending' | 'succeeded' | 'failed';
    error: string | null;
}

const caseOverviewAdapter = createEntityAdapter<CaseOverviewRecord, string>({
    selectId: (record) => record.loan_reference_id
    // Optional sortComparer
})

const initialState: CaseOverviewDataState = caseOverviewAdapter.getInitialState({
    loadingStatus: 'idle',
    error: null,
})

const caseOverviewDataSlice = createSlice({
    name: 'caseOverviewData',
    initialState,
    reducers: {
        addOneCaseOverviewRecord: caseOverviewAdapter.addOne,
        addManyCaseOverviewRecords: caseOverviewAdapter.addMany,
        updateOneCaseOverviewRecord: caseOverviewAdapter.updateOne,
        upsertOneCaseOverviewRecord: caseOverviewAdapter.upsertOne,
        removeOneCaseOverviewRecord: caseOverviewAdapter.removeOne,
        removeAllCaseOverviewRecords: caseOverviewAdapter.removeAll,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCaseOverviewThunk.pending, (state) => {
                state.loadingStatus = 'pending';
                state.error = null;
            })
            .addCase(fetchCaseOverviewThunk.fulfilled, (state, action: PayloadAction<CaseOverviewStepResponse>) => {
                state.loadingStatus = 'succeeded';
                caseOverviewAdapter.setAll(state, action.payload.loan_details);
            })
            .addCase(fetchCaseOverviewThunk.rejected, (state, action) => {
                state.loadingStatus = 'failed';
                // Use adapter.removeAll to clear entities on failure.
                // !!! IMPORTANT: THIS BEHAVIOUR NEEDS TO BE DISCUSSED WITH THE TEAM !!!
                caseOverviewAdapter.removeAll(state);
                if (typeof action.payload === 'string') {
                    state.error = action.payload;
                } else if (action.error?.message) {
                    state.error = action.error.message;
                } else {
                    state.error = 'Unknown error occurred';
                }
            });
    }
});


export const { 
    addOneCaseOverviewRecord,
    addManyCaseOverviewRecords,
    updateOneCaseOverviewRecord,
    upsertOneCaseOverviewRecord,
    removeOneCaseOverviewRecord,
    removeAllCaseOverviewRecords,
    // setCaseOverviewLoading 
  } = caseOverviewDataSlice.actions;

export const caseOverviewDataReducer = caseOverviewDataSlice.reducer;

const caseOverviewSelectors = caseOverviewAdapter.getSelectors<RootState>(
    (state) => state.caseOverviewData
);


export const selectAllCaseOverviewRecords = caseOverviewSelectors.selectAll;
export const selectCaseOverviewRecordById = caseOverviewSelectors.selectById;
export const selectCaseOverviewRecordIds = caseOverviewSelectors.selectIds;
export const selectTotalCaseOverviewRecords = caseOverviewSelectors.selectTotal;

export const selectCaseOverviewLoadingStatus = (state: RootState) => state.caseOverviewData.loadingStatus;
export const selectCaseOverviewError = (state: RootState) => state.caseOverviewData.error;